import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { datricApi } from '../../../services/api/datric.api';
import { fetchBalance } from '../credit/credit.slice';
import { ConsultaResult, HistoryItem } from '../../../models/consulta.model';
import { toastService } from '../../../services/toast.service';

export interface ConsultaState {
	result: ConsultaResult | null;
	loading: boolean;
	error: string | null;
	searched: boolean;
	history: HistoryItem[];
}

const initialState: ConsultaState = {
	result: null,
	loading: false,
	error: null,
	searched: false,
	history: []
};

export const fetchConsultaHistory = createAsyncThunk('consulta/history', async () => {
	return datricApi.consulta.history();
});

export const consultarCpf = createAsyncThunk('consulta/consultar', async (cpf: string, { dispatch, rejectWithValue }) => {
	try {
		const result = await datricApi.consulta.consultar(cpf);
		dispatch(fetchConsultaHistory());
		// A consulta debita créditos no servidor; refaz o saldo para o navbar não
		// ficar defasado até o próximo F5.
		dispatch(fetchBalance());
		return result;
	} catch (e: any) {
		const message = typeof e === 'string' ? e : e?.message || 'Falha ao consultar CPF';
		toastService.error(message);
		return rejectWithValue(message);
	}
});

// Revisualização gratuita de um lead já consultado (dentro de 7 dias).
export const openHistoryLead = createAsyncThunk('consulta/openHistory', async (cpf: string, { rejectWithValue }) => {
	try {
		return await datricApi.consulta.historyDetail(cpf);
	} catch (e: any) {
		const message = typeof e === 'string' ? e : e?.message || 'Falha ao abrir lead do histórico';
		toastService.error(message);
		return rejectWithValue(message);
	}
});

export const consultaSlice = createSlice({
	name: 'consulta',
	initialState,
	reducers: {
		clearConsulta: state => {
			state.result = null;
			state.error = null;
			state.searched = false;
		}
	},
	extraReducers: builder => {
		builder.addCase(consultarCpf.pending, state => {
			state.loading = true;
			state.error = null;
		});
		builder.addCase(consultarCpf.fulfilled, (state, action) => {
			state.loading = false;
			state.result = action.payload;
			state.searched = true;
		});
		builder.addCase(consultarCpf.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as string;
			state.searched = true;
			state.result = null;
		});

		builder.addCase(openHistoryLead.pending, state => {
			state.loading = true;
			state.error = null;
		});
		builder.addCase(openHistoryLead.fulfilled, (state, action) => {
			state.loading = false;
			state.result = action.payload;
			state.searched = true;
		});
		builder.addCase(openHistoryLead.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as string;
		});

		builder.addCase(fetchConsultaHistory.fulfilled, (state, action) => {
			state.history = action.payload as HistoryItem[];
		});
	}
});

export const { clearConsulta } = consultaSlice.actions;
